import * as React from 'react';
import { PropsWithChildren } from 'react';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  FormControlLabel,
  GridLegacy,
  Stack,
  Switch,
  Typography,
  Icon,
  useTheme,
} from '@sbv-clients/ui-shared';

import {
  FacilityEmployeesAggregatesType,
  SaasMetricsType,
} from '../services/reports';

export enum TabSelection {
  all = 1,
  allOnboarded = 2,
  registered = 3,
  registeredDesktop = 4,
  registeredMobile = 5,
  unregistered = 6,
  unregisteredDesktop = 7,
  unregisteredMobile = 8,
}

interface EmployeesStatsHeaderPropsType {
  groupByShift: boolean;
  setGroupByShift: (value: boolean) => void;
  filterByWeekend: boolean;
  setFilterByWeekend: (value: boolean) => void;
  tab: TabSelection;
  setTab: (tab: TabSelection) => void;
  isLoading: boolean;
  isError: boolean;
  error: any;
  data: FacilityEmployeesAggregatesType['employeeMetrics'];
  totalEmployees: SaasMetricsType['totalFacilityEmployeeCount'];
}

function Underline({ active }: { active: boolean }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        top: 44,
        position: 'absolute',
        height: '2px',
        width: 'calc(100% + 4px)',
        borderRadius: '4px 4px 0px 0px',
        backgroundColor: theme.colors.primary.main,
        display: active ? 'inherit' : 'none',
      }}
    />
  );
}

function TabItem({
  tab,
  tabSelection,
  setTab,
  children,
}: PropsWithChildren<{
  tab: TabSelection;
  tabSelection: TabSelection;
  setTab: (tab: TabSelection) => void;
}>) {
  const active = tab === tabSelection;
  const theme = useTheme();
  return (
    <Box sx={{ position: 'relative' }}>
      <ButtonBase
        sx={{
          p: 2,
          color: active ? theme.colors.primary.main : theme.text.secondary,
        }}
        onClick={() => setTab(tabSelection)}
      >
        {children}
      </ButtonBase>
      <Underline active={active} />
    </Box>
  );
}

function Title(props: PropsWithChildren) {
  return (
    <Typography
      fontWeight={(theme) => theme.typography.fontWeightMedium}
      variant={'overline'}
    >
      {props.children}
    </Typography>
  );
}

export function EmployeesStatsHeader({
  tab,
  setTab,
  groupByShift,
  setGroupByShift,
  filterByWeekend,
  setFilterByWeekend,
  isLoading,
  isError,
  error,
  data,
  totalEmployees,
}: EmployeesStatsHeaderPropsType) {
  if (isLoading) {
    return (
      <Box my={6} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert sx={{ my: 6, ml: 3 }} severity={'error'}>
        {get(error, 'message', 'Metrics could not be retrieved')}
      </Alert>
    );
  }

  if (data === undefined || isEmpty(data)) {
    return (
      <Alert sx={{ my: 6, ml: 3 }} severity={'warning'}>
        Metrics could not be retrieved
      </Alert>
    );
  }

  const {
    registeredEmployees,
    totalOnboardedEmployees,
    unregisteredEmployees,
    registeredMobileUsers,
    totalDesktopUsers,
    totalMobileUsers,
    registeredDesktopUsers,
    unregisteredMobileUsers,
    unregisteredDesktopUsers,
  } = data;

  return (
    <GridLegacy
      sx={{
        my: 6,
        pl: 8,
      }}
      container
      justifyContent={'space-between'}
    >
      <GridLegacy xs={'auto'} item container spacing={5}>
        <GridLegacy
          spacing={{ xs: 1, xl: 3 }}
          xs={'auto'}
          item
          container
          direction={'column'}
          justifyContent={'flex-start'}
          pb={4}
        >
          <GridLegacy
            item
            sx={{
              ml: 4,
            }}
          >
            <Title>All</Title>
          </GridLegacy>
          <GridLegacy item>
            <TabItem tab={tab} tabSelection={TabSelection.all} setTab={setTab}>
              <Typography>{totalEmployees} Total</Typography>
            </TabItem>
          </GridLegacy>
        </GridLegacy>
        <Divider
          sx={{
            ml: { xs: 5, xl: 7 },
            mr: { xs: 2, xl: 7 },
            width: 2,
          }}
          orientation={'vertical'}
          flexItem
          variant={'middle'}
        />
        <GridLegacy
          spacing={{ xs: 1, xl: 3 }}
          xs={'auto'}
          item
          container
          direction={'column'}
          justifyContent={'flex-start'}
          pb={4}
        >
          <GridLegacy item>
            <Title>Onboarded</Title>
          </GridLegacy>
          <GridLegacy item>
            <TabItem
              tab={tab}
              tabSelection={TabSelection.allOnboarded}
              setTab={setTab}
            >
              <Typography>{totalOnboardedEmployees} Onboarded</Typography>
            </TabItem>
          </GridLegacy>
        </GridLegacy>

        <Divider
          sx={{
            ml: { xs: 5, xl: 7 },
            mr: { xs: 2, xl: 7 },
            width: 2,
          }}
          orientation={'vertical'}
          flexItem
          variant={'middle'}
        />

        <GridLegacy
          spacing={{ xs: 1, xl: 3 }}
          xs={'auto'}
          item
          container
          direction={'column'}
          justifyContent={'flex-start'}
          pb={4}
        >
          <GridLegacy item>
            <Title>Registered</Title>
          </GridLegacy>
          <GridLegacy
            spacing={{ xs: 1, xl: 4 }}
            item
            container
            direction={'row'}
          >
            <GridLegacy item>
              <TabItem
                tab={tab}
                setTab={setTab}
                tabSelection={TabSelection.registered}
              >
                <Typography>
                  {registeredEmployees}/{totalOnboardedEmployees} Total
                </Typography>
              </TabItem>
            </GridLegacy>
            <GridLegacy item>
              <TabItem
                tab={tab}
                setTab={setTab}
                tabSelection={TabSelection.registeredDesktop}
              >
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                  <Icon name={'DesktopWindowsOutlined'} fontSize={'small'} />
                  <Typography>
                    {registeredDesktopUsers}/{totalDesktopUsers}
                  </Typography>
                </Stack>
              </TabItem>
            </GridLegacy>
            <GridLegacy item>
              <TabItem
                tab={tab}
                setTab={setTab}
                tabSelection={TabSelection.registeredMobile}
              >
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                  <Icon name={'PhoneIphoneOutlined'} fontSize={'small'} />
                  <Typography>
                    {registeredMobileUsers}/{totalMobileUsers}
                  </Typography>
                </Stack>
              </TabItem>
            </GridLegacy>
          </GridLegacy>
        </GridLegacy>
        <Divider
          sx={{
            ml: { xs: 2, xl: 7 },
            mr: { xs: 2, xl: 7 },
            width: 2,
          }}
          orientation={'vertical'}
          flexItem
          variant={'middle'}
        />
        <GridLegacy
          spacing={{ xs: 1, xl: 3 }}
          xs={'auto'}
          item
          container
          direction={'column'}
          justifyContent={'flex-start'}
          pb={4}
        >
          <GridLegacy item>
            <Title>Unregistered</Title>
          </GridLegacy>
          <GridLegacy
            spacing={{ xs: 1, xl: 4 }}
            item
            container
            direction={'row'}
          >
            <GridLegacy item>
              <TabItem
                tab={tab}
                setTab={setTab}
                tabSelection={TabSelection.unregistered}
              >
                <Typography>
                  {unregisteredEmployees}/{totalOnboardedEmployees} Total
                </Typography>
              </TabItem>
            </GridLegacy>
            <GridLegacy item>
              <TabItem
                tab={tab}
                setTab={setTab}
                tabSelection={TabSelection.unregisteredDesktop}
              >
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                  <Icon name={'DesktopWindowsOutlined'} fontSize={'small'} />
                  <Typography>
                    {unregisteredDesktopUsers}/{totalDesktopUsers}
                  </Typography>
                </Stack>
              </TabItem>
            </GridLegacy>
            <GridLegacy item>
              <TabItem
                tab={tab}
                setTab={setTab}
                tabSelection={TabSelection.unregisteredMobile}
              >
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                  <Icon name={'PhoneIphoneOutlined'} fontSize={'small'} />
                  <Typography>
                    {unregisteredMobileUsers}/{totalMobileUsers}
                  </Typography>
                </Stack>
              </TabItem>
            </GridLegacy>
          </GridLegacy>
        </GridLegacy>
      </GridLegacy>
      <GridLegacy sm={'auto'} xs={12} item container spacing={5}>
        <GridLegacy mt={3} spacing={1} item container direction={'column'}>
          <GridLegacy item>
            <Switch
              checked={groupByShift}
              onChange={(event) => setGroupByShift(event.target.checked)}
              rightLabel="Group by Shift"
            />
          </GridLegacy>
          <GridLegacy item>
            <FormControlLabel
              control={
                <Switch
                  checked={filterByWeekend}
                  onChange={(event) => setFilterByWeekend(event.target.checked)}
                />
              }
              label="Filter by Weekend"
            />
          </GridLegacy>
        </GridLegacy>
      </GridLegacy>
    </GridLegacy>
  );
}
